import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTheme } from '@mui/material';
import cn from '../../../utils/cn';
import PencilSvgIcon from '../../../icons/PencilSvgIcon';
import SkillAliasesModal from '../SkillAliasesModal';

export const SkillsModal = ({
  skills,
  setSkills,
  mandatorySkills,
  setMandatorySkills,
  preferredSkills,
  setPreferredSkills,
  newSkills,
  setNewSkills,
  suggestedSkills,
  aliasSkills = {},
  setAliasSkills,
  isDuplicateSKill,
  setIsDuplicateSkill,
}) => {
  const skillsContainerRef = useRef(null);
  const theme = useTheme();
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [focusedSkill, setFocusedSkill] = useState(null);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [currentSkillForAlias, setCurrentSkillForAlias] = useState('');

  useEffect(() => {
    setSkills([...new Set([...skills, ...mandatorySkills, ...preferredSkills])]);
    if (mandatorySkills.length === 0 && preferredSkills.length === 0) {
      setMandatorySkills([...skills])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isSkillDuplicate = (newSkillName, currentIndex = -1) => {
    const trimmedNewSkill = newSkillName.trim().toLowerCase();
    if (!trimmedNewSkill) return false;

    const existsInOriginalSkills = skills.some(skill =>
      skill.toLowerCase() === trimmedNewSkill
    );

    const existsInOtherNewSkills = newSkills.some((skill, index) =>
      index !== currentIndex && skill.name.trim().toLowerCase() === trimmedNewSkill
    );

    return existsInOriginalSkills || existsInOtherNewSkills;
  };

  const handleSkillSelection = (skillName, type) => {
    if (type === "mandatory") {
      setMandatorySkills((prev) =>
        prev.includes(skillName)
          ? prev.filter((skill) => skill !== skillName)
          : [...prev, skillName]
      );
      setPreferredSkills((prev) => prev.filter((skill) => skill !== skillName));
    } else if (type === "preferred") {
      setPreferredSkills((prev) =>
        prev.includes(skillName)
          ? prev.filter((skill) => skill !== skillName)
          : [...prev, skillName]
      );
      setMandatorySkills((prev) => prev.filter((skill) => skill !== skillName));
    }
  };

  const handleAddNewSkillInput = () => {
    if (
      newSkills.length === 0 ||
      newSkills[newSkills.length - 1].name.trim() !== ""
    ) {
      setNewSkills([...newSkills, { name: "", type: "" }]);
    }
  };

  const handleNewSkillChange = (index, value) => {
    const updatedSkills = [...newSkills];
    updatedSkills[index].name = value;
    setNewSkills(updatedSkills);
  };

  const handleSkillTypeChange = (index, type) => {
    const skillName = newSkills[index].name.trim();
    if (skillName && isSkillDuplicate(skillName, index)) {
      return;
    }

    setNewSkills((prevSkills) => {
      return prevSkills.map((skill, i) =>
        i === index
          ? { ...skill, type: skill.type === type ? "" : type }
          : skill
      );
    });

    setMandatorySkills((prev) => {
      if (type === "mandatory") {
        return prev.includes(skillName)
          ? prev.filter((skill) => skill !== skillName)
          : [...prev, skillName];
      }
      return prev.filter((skill) => skill !== skillName);
    });

    setPreferredSkills((prev) => {
      if (type === "preferred") {
        return prev.includes(skillName)
          ? prev.filter((skill) => skill !== skillName)
          : [...prev, skillName];
      }
      return prev.filter((skill) => skill !== skillName);
    });
  };

  const handleRemoveNewSkillInput = (index) => {
    const skillToRemove = newSkills[index].name.trim();

    setNewSkills(newSkills.filter((_, i) => i !== index));

    if (skillToRemove !== "") {
      setMandatorySkills(prev => prev.filter(skill => skill !== skillToRemove));
      setPreferredSkills(prev => prev.filter(skill => skill !== skillToRemove));
    }
  };

  useEffect(() => {
    if (skillsContainerRef.current && newSkills && newSkills.length > 0) {
      skillsContainerRef.current.scrollTop = skillsContainerRef.current.scrollHeight;
    }
  }, [newSkills]);

  const handleSelectAll = (type) => {
    const validNewSkills = newSkills
      .map((s, index) => ({ name: s.name.trim(), index }))
      .filter(({ name, index }) => name !== "" && !isSkillDuplicate(name, index))
      .map(({ name }) => name);

    if (type === "mandatory") {
      const newMandatorySkills =
        mandatorySkills.length === skills.length + validNewSkills.length
          ? []
          : [...skills, ...validNewSkills];

      setMandatorySkills(newMandatorySkills);
      setPreferredSkills([]);

      setNewSkills((prevSkills) =>
        prevSkills.map((skill, index) =>
          skill.name.trim() !== "" && !isSkillDuplicate(skill.name, index)
            ? { ...skill, type: newMandatorySkills.length ? "mandatory" : "" }
            : skill
        )
      );
    } else if (type === "preferred") {
      const newPreferredSkills =
        preferredSkills.length === skills.length + validNewSkills.length
          ? []
          : [...skills, ...validNewSkills];

      setPreferredSkills(newPreferredSkills);
      setMandatorySkills([]);

      setNewSkills((prevSkills) =>
        prevSkills.map((skill, index) =>
          skill.name.trim() !== "" && !isSkillDuplicate(skill.name, index)
            ? { ...skill, type: newPreferredSkills.length ? "preferred" : "" }
            : skill
        )
      );
    }
  };

  const handleAddSuggestedSkill = (suggestedSkill) => {
    const isDuplicate = isSkillDuplicate(suggestedSkill);

    if (isDuplicate) return;

    if (
      newSkills.length === 0 ||
      newSkills[newSkills.length - 1].name.trim() !== ""
    ) {
      setNewSkills([...newSkills, { name: suggestedSkill, type: "" }]);
    } else {
      const updatedSkills = [...newSkills];
      updatedSkills[updatedSkills.length - 1].name = suggestedSkill;
      setNewSkills(updatedSkills);
    }
  };

  const handleEditAliases = (skillName) => {
    setCurrentSkillForAlias(skillName);
    setShowAliasModal(true);
  };

  const handleSubmitAliases = (skillName, aliases) => {
    setAliasSkills(prev => ({
      ...prev,
      [skillName]: aliases
    }));
  };

  const handleCloseAliasModal = () => {
    setShowAliasModal(false);
    setCurrentSkillForAlias('');
  };

  useEffect(() => {
    const hasDuplicate = newSkills.some((skill, index) =>
      skill.name.trim() && isSkillDuplicate(skill.name, index)
    );
    setIsDuplicateSkill(hasDuplicate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSkills, setIsDuplicateSkill]);

  return (
    <>
      <div
        className="px-5 sm:px-14 py-9"
        style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.text.main }}
      >
        {(skills && skills.length > 0) || (newSkills && newSkills.length > 0) ? (
          <div className="flex flex-col">
            <div className="flex items-center gap-2 sm:gap-4 md:gap-6 lg:gap-10">
              <div className="flex-1">
                <h2 className="font-bold">Select Skills</h2>
              </div>
              <div className="w-20 md:w-32 text-center">
                <h3 className="">Mandatory Skill</h3>
              </div>
              <div className="w-20 md:w-32 text-center">
                <h3 className="">Preferred Skill</h3>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 md:gap-6 lg:gap-10">
              <div className="flex-1"></div>
              <div className="w-20 md:w-32 text-center pb-2">
                <button onClick={() => handleSelectAll("mandatory")} className="text-xs font-medium">
                  {mandatorySkills.length === (skills.length + newSkills.length) ? "Remove All" : "Select All"}
                </button>
              </div>
              <div className="w-20 md:w-32 text-center pb-2">
                <button onClick={() => handleSelectAll("preferred")} className="text-xs font-medium">
                  {preferredSkills.length === (skills.length + newSkills.length) ? "Remove All" : "Select All"}
                </button>
              </div>
            </div>

            <div
              ref={skillsContainerRef}
              className="relative max-h-48 overflow-y-auto always-not-show-scrollbar-horizontal"
            >
              {skills.map((skill) => (
                <div
                  key={skill}
                  className="flex items-center pb-2 gap-2 sm:gap-4 md:gap-6 lg:gap-10 relative"
                  onMouseEnter={() => setHoveredSkill(skill)}
                  onMouseLeave={() => setHoveredSkill(null)}
                >
                  <button
                    className="flex items-center gap-4 flex-1 relative px-1"
                    style={{ width: "calc(100% - 160px)", minWidth: 0 }}
                    onFocus={() => setFocusedSkill(skill)}
                    onBlur={() => setFocusedSkill(null)}
                    onClick={() => handleEditAliases(skill)}
                    tabIndex={0}
                    aria-label={`${skill} - Press Enter to edit aliases`}
                  >
                    <span
                      className="block overflow-hidden whitespace-nowrap text-ellipsis"
                      style={{ color: theme.palette.text.main }}
                      title={skill}
                    >
                      {skill}
                    </span>
                    {(hoveredSkill === skill || focusedSkill === skill) && (
                      <span className='text-xs flex gap-1 items-center'>
                        <PencilSvgIcon color={theme.palette.text.icon} />
                        <span className='whitespace-nowrap'>
                          Edit aliases
                        </span>
                      </span>
                    )}
                  </button>
                  <div className="w-20 md:w-32 flex justify-center">
                    <input
                      type="checkbox"
                      aria-label={`Mark ${skill} as mandatory skill`}
                      className="w-4 h-4 appearance-none border border-black checked:bg-accent-gray-light rounded-sm "
                      checked={mandatorySkills.includes(skill)}
                      onChange={() => handleSkillSelection(skill, "mandatory")}
                    />
                  </div>
                  <div className="w-20 md:w-32 flex justify-center">
                    <input
                      type="checkbox"
                      aria-label={`Mark ${skill} as preferred skill`}
                      className="w-4 h-4 appearance-none border border-black checked:bg-accent-gray-light rounded-sm "
                      checked={preferredSkills.includes(skill)}
                      onChange={() => handleSkillSelection(skill, "preferred")}
                    />
                  </div>
                </div>
              ))}

              {newSkills.map((skill, index) => {
                const isDuplicate = skill.name.trim() && isSkillDuplicate(skill.name, index);
                return (
                  <div
                    key={index}
                    className="flex items-center pb-2 gap-2 sm:gap-4 md:gap-6 lg:gap-10 relative"
                    style={{ borderColor: theme.palette.secondary.v3 }}
                    onMouseEnter={() => skill.name.trim() && setHoveredSkill(skill.name)}
                    onMouseLeave={() => setHoveredSkill(null)}
                  >
                    <div
                      className="flex-1 relative"
                      style={{ width: "calc(100% - 160px)", minWidth: 0 }}
                    >
                      <div className="relative">
                        <input
                          type="text"
                          value={skill.name}
                          onChange={(e) => handleNewSkillChange(index, e.target.value)}
                          placeholder="Enter skill"
                          className={`border rounded-lg px-2 py-1 w-full`}
                          style={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.text.main,
                            borderColor: isDuplicate ? theme.palette.error.main : theme.palette.secondary.v3,
                            minWidth: 0,
                          }}
                          aria-label="Enter new skill name"
                        />
                        {skill.name.trim() && !isDuplicate && (
                          <button
                            onClick={() => handleEditAliases(skill.name)}
                            onFocus={() => setFocusedSkill(skill.name)}
                            onBlur={() => setFocusedSkill(null)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 rounded p-1"
                            tabIndex={0}
                            aria-label={`Edit aliases for ${skill.name}`}
                          >
                            <PencilSvgIcon color={theme.palette.text.icon} />
                          </button>
                        )}
                      </div>
                      {isDuplicate && (
                        <div className="text-xs text-red-500">
                          Skill already exists
                        </div>
                      )}
                    </div>
                    <div className="w-20 md:w-32 flex justify-center">
                      <input
                        type="checkbox"
                        aria-label={`Mark ${skill.name || 'new skill'} as mandatory skill`}
                        className="w-4 h-4 appearance-none border border-black checked:bg-accent-gray-light rounded-sm "
                        checked={skill.name.trim() !== "" && skill.type === "mandatory" && !isDuplicate}
                        onChange={() => skill.name.trim() !== "" && !isDuplicate && handleSkillTypeChange(index, "mandatory")}
                        disabled={isDuplicate}
                      />
                    </div>
                    <div className="w-20 md:w-32 flex justify-center relative">
                      <div className="flex items-center relative">
                        <input
                          type="checkbox"
                          aria-label={`Mark ${skill.name || 'new skill'} as preferred skill`}
                          className="w-4 h-4 appearance-none border border-black checked:bg-accent-gray-light rounded-sm "
                          checked={skill.name.trim() !== "" && skill.type === "preferred" && !isDuplicate}
                          onChange={() => skill.name.trim() !== "" && !isDuplicate && handleSkillTypeChange(index, "preferred")}
                          disabled={isDuplicate}
                        />
                        <button
                          onClick={() => handleRemoveNewSkillInput(index)}
                          className="absolute top-1/2 -translate-y-1/2 -right-11"
                          aria-label={`Remove ${skill.name || 'new skill'} input`}
                          tabIndex={0}
                        >
                          <XMarkIcon className="text-red-700 h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="flex items-center justify-center py-8 text-center">
              <p>No skills available. Add new skills below.</p>
            </div>
          </div>
        )}

        <div className="mt-4">
          <button
            type="button"
            onClick={handleAddNewSkillInput}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.dark,
              borderColor: theme.palette.secondary.v3,
            }}
            className={cn(
              "border py-1 px-2 rounded-full",
              newSkills.length > 0 && newSkills[newSkills.length - 1].name.trim() === "" && "text-accent-gray-medium cursor-not-allowed"
            )}
            disabled={(newSkills.length > 0 && newSkills[newSkills.length - 1].name.trim() === "") || isDuplicateSKill}
            aria-label="Add new skill"
          >
            <span className="flex gap-2 justify-center items-center text-black">
              Add New Skill <PlusIcon className="w-4 h-4" />
            </span>
          </button>
        </div>

        {suggestedSkills && suggestedSkills.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium mb-2">Suggested Skills:</h3>
            <div className="flex gap-2 w-full overflow-x-auto pb-2 whitespace-nowrap always-show-scrollbar-horizontal">
              {suggestedSkills.map((suggestedSkill, index) => (
                <button
                  key={index}
                  onClick={() => handleAddSuggestedSkill(suggestedSkill)}
                  className="border rounded-full px-3 py-1 text-sm"
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.main,
                    borderColor: theme.palette.secondary.v3,
                  }}
                  aria-label={`Add suggested skill: ${suggestedSkill}`}
                >
                  {suggestedSkill}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <SkillAliasesModal
        isOpen={showAliasModal}
        onClose={handleCloseAliasModal}
        skillName={currentSkillForAlias}
        existingAliases={aliasSkills[currentSkillForAlias] || []}
        onSubmit={handleSubmitAliases}
      />
    </>
  );
};
